// Shared, side-effect-free policy for initial HTML and hydrated metadata.
export const PUBLIC_HOSTS = new Set([
  "bookaclassic.com", "www.bookaclassic.com", "driveshare.com", "www.driveshare.com",
]);
const LANGUAGE_CODES = new Set(["en", "fr", "de", "da", "sv", "nb", "no", "fi", "nl", "it", "es", "pt", "pl", "cs", "sk", "bg"]);
const CATEGORY_MARKETS = new Set(["uk", "ie", "dk", "se", "nz", "au", "pt", "it", "ca", "de", "fr", "za", "mt", "es", "br", "pl", "sk", "no", "bg", "co", "fi", "nl", "be", "ec", "mx", "cz", "in"]);
export const NON_INDEXABLE_EDITORIAL_PATHS = new Set(["/nz/70"]);
// Reviewed publication exclusions override CMS flags and canonical ownership.
// Canada acquisition is not a launch page, even when its CMS row is indexable.
import { REVIEWED_PUBLICATION_EXCLUSIONS } from "../../../../lib/copy/src/public-record-indexability.mjs";
// Compatibility projection of the source catalogue, never another decision list.
export const REVIEWED_NOINDEX_PATHS = new Set(REVIEWED_PUBLICATION_EXCLUSIONS.map(record=>record.canonicalPath));

export function isNonProductionSeoHost(host) {
  return !PUBLIC_HOSTS.has(normalizeSeoHost(host));
}

export function normalizeSeoHost(value) {
  if (typeof value !== "string" || !value || value !== value.trim() ||
      !/^[a-z0-9.-]+(?::[0-9]{1,5})?$/i.test(value)) return "";
  const [host, port] = value.toLowerCase().split(":");
  if (port && (Number(port) < 1 || Number(port) > 65535)) return "";
  if (host.length > 253 || host.split(".").some(label =>
    !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label))) return "";
  return host;
}

// A forwarding header can restrict authority, never upgrade a noncanonical
// direct Host. No documented sanitized edge-header contract exists here.
export function seoRequestHost(request) {
  const host = normalizeSeoHost(request.headers?.host);
  const forwarded = request.headers?.["x-forwarded-host"];
  return forwarded === undefined || normalizeSeoHost(forwarded) === host ? host : "";
}

export const NONCANONICAL_ROBOTS = "User-agent: *\nDisallow: /\n";
export const NONCANONICAL_ORIGIN = "https://www.bookaclassic.com";

// The caller supplies the already resolved production equivalent, not a
// hostname inferred from a preview's name. Only known production origins may
// contribute a path; queries, fragments and credentials never survive.
export function noncanonicalSeoCanonical(pathname, authoritative) {
  let path = String(pathname || "/").split(/[?#]/)[0];
  try {
    if (typeof authoritative !== "string" || !authoritative) throw new Error("No resolved equivalent");
    const url = new URL(authoritative, NONCANONICAL_ORIGIN);
    if (!url.username && !url.password && PUBLIC_HOSTS.has(url.hostname) &&
        ["http:", "https:"].includes(url.protocol)) path = url.pathname;
  } catch {}
  if (!path.startsWith("/") || path.startsWith("//") || /[\\\s<>"'\u0000-\u001f]/.test(path)) path = "/";
  return `${NONCANONICAL_ORIGIN}${path}`;
}

export function isAccountSeoPath(path) {
  return /^\/(?:[a-z]{2}\/)?(?:signup|sign-up|login|sign-in|register|password-reset)(?:\/|$)/i.test(path);
}

export function isSearchSeoPath(path) {
  return /^\/(?:[a-z]{2}\/)?(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:search|s)(?:\/|$)/i.test(path);
}

export function isUnsupportedLanguagePath(path, markets) {
  const [market, language] = String(path).toLowerCase().split("/").filter(Boolean);
  const meta = markets[market];
  if (!meta || !LANGUAGE_CODES.has(language)) return false;
  return !meta.languages.some((value) => value.toLowerCase().split("-")[0] === language);
}

export function categorySeoTitle(title, path, language = "en") {
  // Prepared native titles already carry an authored native country label.
  // Never append English metadata to a translated document.
  if (String(language).toLowerCase().split("-")[0] !== "en") return title;
  const match = String(path).match(/^\/([a-z]{2})\/(?:en\/)?category\/[^/]+\/?$/);
  if (!match || !CATEGORY_MARKETS.has(match[1])) return title;
  const region = match[1] === "uk" ? "GB" : match[1].toUpperCase();
  const country = new Intl.DisplayNames(["en"], { type: "region" }).of(region);
  if (!country || title.includes(country)) return title;
  const separator = title.indexOf(" |");
  return separator >= 0
    ? `${title.slice(0, separator)} in ${country}${title.slice(separator)}`
    : `${title} in ${country}`;
}

export function rewriteRetiredEditorialLinks(html) {
  // Rewrite rendered anchors only. Captured source URLs, attribution, quotes
  // and provenance hashes remain unchanged.
  return String(html).replace(/(\bhref\s*=\s*)(["'])(https?:\/\/[^"']+)\2/gi, (match, prefix, quote, href) => {
    let url;
    try { url = new URL(href.replace(/&amp;/g, "&")); } catch { return match; }
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    let target;
    if (host === "support.driveshare.com") target = "/help";
    else if (host === "drivevinty.com" && /^\/(?:s|search)\/?$/.test(url.pathname)) {
      const search = new URLSearchParams({ country: "US" });
      const address = url.searchParams.get("address");
      if (address) search.set("q", address.split(",")[0].trim());
      const latitude = url.searchParams.get("search_lat") || url.searchParams.get("lat");
      const longitude = url.searchParams.get("search_lng") || url.searchParams.get("lng");
      if (latitude && longitude && Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude))) {
        search.set("from_lat", latitude);
        search.set("from_lng", longitude);
      }
      for (const key of ["rental_type", "sort"]) {
        if (url.searchParams.has(key)) search.set(key, url.searchParams.get(key));
      }
      target = `/search?${search}`;
    }
    if (!target) return match;
    return `${prefix}${quote}${target.replace(/&/g, "&amp;")}${quote}`;
  });
}